import { gql } from '@urql/core';

import { graphql } from '../../gql';
import { COMPOSITION_GET_DATA_V3 } from '../queries/composition-v3.query';

export const HEALTH_CHECK_FRAGMENTS = {
  CompositionV3: graphql(`
    query CompositionV3HealthCheck(
      $entities: [CompositionV3Ent!]!
      $filters: CompositionV3Filters!
      $corrections: CompositionV3Corrections!
      $role_taxonomy: Int!
      $skill_taxonomy: Int!
      $breakdown_options: CompositionV3BreakdownOptions!
    ) {
      composition_v3(
        req: {
          entities: $entities
          filters: $filters
          corrections: $corrections
          role_taxonomy: $role_taxonomy
          skill_taxonomy: $skill_taxonomy
          breakdown_options: $breakdown_options
        }
      ) {
        entities {
          id
          short_name
          long_name
          metrics {
            headcount {
              id
              val
            }
            growth_rate {
              id
              val
            }
            hiring_rate {
              id
              val
            }
            attrition_rate {
              id
              val
            }
          }
        }
      }
    }
  `),
  CompositionV4: graphql(`
    query CompositionV4HealthCheck($req: CompositionV4Req!) {
      composition_v4(req: $req) {
        entities {
          id
          short_name
          long_name
          metrics {
            headcount {
              id
              val
            }
            growth_rate {
              id
              val
            }
            hiring_rate {
              id
              val
            }
            attrition_rate {
              id
              val
            }
          }
          breakdowns {
            dimension
            level
            categories {
              id
              short_name
              headcount {
                id
                count
                share
              }
            }
          }
        }
      }
    }
  `),
  Auth: graphql(`
    query AuthHealthCheck($ids: [String]) {
      users(ids: $ids) {
        id
        name
        username
        email
        role
        client_name
        live
        active
        tabs
        company_lists
        linkup_postings
        unified_postings
        num_seats
        custom_taxonomy
        metadata {
          key
          value
        }
      }
    }
  `),
  MonthWeekIndex: graphql(`
    query MonthWeekIndexHealthCheck {
      index {
        startMonthIndex
        endMonthIndex
        startWeekIndex
        endWeekIndex
        maxWeekIndex
        endMonth
        maxWeek
      }
    }
  `),
  CompositionSummaryData: graphql(`
    query CompositionSummaryV3Data(
      $entities: [CompositionV3Ent!]!
      $filters: CompositionV3Filters!
      $corrections: CompositionV3Corrections!
      $role_taxonomy: Int!
      $skill_taxonomy: Int!
      $breakdown_options: CompositionV3BreakdownOptions!
    ) {
      composition_v3(
        req: {
          entities: $entities
          filters: $filters
          corrections: $corrections
          role_taxonomy: $role_taxonomy
          skill_taxonomy: $skill_taxonomy
          breakdown_options: $breakdown_options
        }
      ) {
        entities {
          id
          short_name
          long_name
          metrics {
            headcount {
              id
              val
            }
            growth_rate {
              id
              val
            }
            hiring_rate {
              id
              val
            }
            attrition_rate {
              id
              val
            }
          }
        }
      }
    }
  `),
  PostingsRoles: graphql(`
    query EntityPostingsRolesHealthCheck(
      $filters: PostingFilters
      $dim1: Dimension1
    ) {
      posting(filters: $filters, dim1: $dim1, dim2: top) {
        metadata {
          id
          shortName
          longName
          __typename
        }
        category {
          metadata {
            id
            shortName
            longName
            __typename
          }
          metrics {
            count
            share
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  // --- selectionList: one probe per live caller ---
  // The op is deprecated in the public docs in favour of `taxonomy`, but the
  // dashboard has not migrated off it. Rather than one probe standing for all
  // of it, each surviving caller gets its own check so the board says WHERE a
  // failure lands. Geography, RICS and demographics are absent on purpose —
  // those moved to taxonomyTree + the reference lists, so probing them here
  // would prove nothing. All four use plain `gql`, not the typed `graphql()`
  // tag: `graphql()` resolves through the generated registry and returns `{}`
  // for a document codegen has never seen, which the runner would then
  // execute as an empty query.
  //
  // Live, NOT deprecated on the board — DATE_SELECTION_LIST_QUERY, whose
  // response `useDateRefs` turns into the index → label map CustomDash uses
  // to name the month/week indices plot data comes back with.
  SelectionListDates: gql(`
    query SelectionListDatesHealthCheck {
      selectionList {
        week {
          id
          shortName
        }
        month {
          id
          shortName
        }
      }
    }
  `),
  // Live, NOT deprecated on the board — CurrencySelectionList, read by the
  // currency preference editor for the currency list and its rates.
  SelectionListCurrency: gql(`
    query SelectionListCurrencyHealthCheck {
      selectionList {
        currency {
          id
          code
          name
          rate
          record_date
          effective_date
        }
      }
    }
  `),
  // DataBuilder's V2 role and skill selection lists —
  // ROLE_V2_SELECTION_LIST_QUERY and SKILL_V2_SELECTION_LIST_QUERY (the
  // latter behind `useSkillSelectionLists`). `role_k50` and `skill_k75` stand
  // in for the deeper tiers those callers also request (role_k150..k1500,
  // skill_k700/k3000): same resolver, and probing every tier would drag
  // thousands of rows through a half-hourly sweep.
  SharedSelectionList: gql(`
    query SharedSelectionListHealthCheck {
      selectionList {
        job_category {
          id
          shortName
          longName
          label: longName
          topCleanedTitles
          topRawTitles
        }
        role_k50 {
          id
          shortName
          longName
          label: longName
          topCleanedTitles
          topRawTitles
          job_category: roleK7
        }
        skill_k75 {
          id
          shortName
          longName
          topSkills
        }
      }
    }
  `),
  CompanyIndustrySelectionList: graphql(`
    query CompanyIndustySelectionListHealthCheck {
      selectionList {
        company {
          id
          shortName
          rank
          ticker
          longName
          label: longName
          industry: industryId
          ricsK50Id
          rics_k50: ricsK50Id
          ricsK400Id
          rics_k400: ricsK400Id
          sedol
          isin
          rcid
        }
        industry {
          id
          shortName
          longName
          label: longName
        }
        rics_k10 {
          id
          shortName
          label: longName
          longName
        }
        rics_k50 {
          id
          shortName
          longName
          label: longName
          rics_k10
        }
        rics_k400 {
          id
          shortName
          longName
          rics_k10
          rics_k50
        }
      }
    }
  `),
  GeographySummaryMapData: graphql(`
    query GeographySummaryMapData($query: MapCoordsReq!) {
      mapCoords(req: $query) {
        region {
          id
          name
          lat
          lon
        }
        country {
          id
          name
          lat
          lon
        }
        msa {
          id
          name
          lat
          lon
        }
      }
    }
  `),
  Screener: graphql(`
    query GetScreenerDataHealthCheck($filters: ScreenerReq!) {
      screener(filters: $filters) {
        pagination {
          total_results
          __typename
        }
        entities {
          short_name
          long_name
          entity_id
          headcount
          inflow
          outflow
          hiring
          attrition
          growth
          growth_yoy
          salary
          tenure
          entity_info {
            company_info {
              description
              email
              founder_api
              founder_black
              founder_female
              founder_hispanic
              founder_male
              founder_multiple
              founder_native
              founder_white
              funding_stage
              hq_country
              hq_metro_area
              hq_region
              hq_state
              is_public
              last_funding_amount
              last_funding_year
              linkedin_url
              phone_number
              rcid
              rics_k10
              rics_k50
              rics_k400
              total_funding_amount
              ultimate_parent_name
              ultimate_parent_rcid
              valuation
              website
              year_founded
              __typename
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  UserSearchHealthCheck: graphql(`
    query UserSearchHealthCheck($input: String!) {
      userSearch(input: $input) {
        id
        name
        email
        client_name
        active
        live
        __typename
      }
    }
  `),
  ReportConfigHealthCheck: graphql(`
    query ReportConfigHealthCheck {
      reports_validation {
        report_validation {
          id
          report_type
          label
          description
          sample_link
          category
          main_entity {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          comparison_entities {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          filters {
            selection_list
            limit
            required
            label
            description
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `),
  TalentDiscoveryV3: graphql(`
    query TalentDiscoveryV3HealthCheck($query: TalentDiscoveryReqV3) {
      talent_discovery_search_v3(query: $query) {
        columns
        users {
          RCID
          first_name
          last_name
          current_company
          current_title
          email
          user_location
          salary_total
          __typename
        }
        pagination {
          tot_pages_num
          tot_users_num
          __typename
        }
        __typename
      }
    }
  `),

  // --- Public GraphQL API surface checks ---
  // These fragments exist to probe every non-deprecated op the public API
  // docs expose (tools/scripts/spectaql/obscured-introspection.json) — the
  // sweep measures the surface we ask consumers to use, so an op's probe
  // retires when it is deprecated, unless the dashboard itself still calls it
  // (`selectionList`). They use the plain `gql` tag rather than
  // the typed `graphql()` tag on purpose: `graphql()` resolves documents
  // through the generated registry in ../../gql/gql.ts and returns `{}`
  // for any string codegen hasn't seen, which the health-check runner
  // would then execute as an empty document. Health checks never consume
  // typed results, so the registry buys nothing here — and every one of
  // these documents is validated against the committed introspection
  // schema in health-check-alignment.spec.ts.
  EntityV3: gql(`
    query EntityV3HealthCheck($req: EntityV3Req) {
      entityV3(req: $req) {
        entity {
          name
          description
          founded
          headquarters
        }
        competitors {
          id
          shortName
          longName
          closeness
        }
      }
    }
  `),
  PayV3: gql(`
    query PayV3HealthCheck($req: PayV3Req) {
      pay_v3(req: $req) {
        entities {
          id
          short_name
        }
        refs {
          months
        }
      }
    }
  `),
  PostingsV3: gql(`
    query PostingsV3HealthCheck($req: PostingsV3Req) {
      postings_v3(req: $req) {
        entities {
          id
          index
          short_name
        }
      }
    }
  `),
  TransitionsV3: gql(`
    query TransitionsV3HealthCheck($req: TransitionsV3Req) {
      transitions_v3(req: $req) {
        entities {
          id
          index
          short_name
        }
      }
    }
  `),
  SentimentV3: gql(`
    query SentimentV3HealthCheck($req: SentimentV3Req) {
      sentiment_v3(req: $req) {
        entities {
          id
          index
          short_name
        }
      }
    }
  `),
  // The page's own composition document (typed, already in the generated
  // map), so the probe exercises exactly what market-conditions charts read.
  CompositionV3PageData: COMPOSITION_GET_DATA_V3,
  PostingDiscoverySearch: gql(`
    query PostingDiscoverySearchHealthCheck($query: PostingDiscoveryReq) {
      posting_discovery_search(query: $query) {
        pagination {
          tot_postings_num
        }
        postings {
          company_name
          city
        }
      }
    }
  `),
  TalentDiscoveryNameRecommendationsV3: gql(`
    query NameRecommendationsV3HealthCheck($query: TalentDiscoveryReqV3) {
      talent_discovery_name_recommendations_v3(query: $query) {
        names {
          id
          name
        }
      }
    }
  `),
  TalentDiscoveryByIdV3: gql(`
    query TalentDiscoveryByIdV3HealthCheck($query: TalentDiscoveryByIdReqV3) {
      talent_discovery_search_by_id_v3(query: $query) {
        users {
          original_user_id
          user_id
          first_name
          last_name
        }
      }
    }
  `),
  Taxonomy: gql(`
    query TaxonomyHealthCheck($req: TaxonomyRequest!) {
      taxonomy(req: $req) {
        levels {
          id
          label
        }
        nodes {
          id
          name
        }
      }
    }
  `),
  TaxonomyList: gql(`
    query TaxonomyListHealthCheck($req: TaxonomyListRequest!) {
      taxonomyList(req: $req) {
        taxonomies {
          id
          name
          version
        }
      }
    }
  `),
  TaxonomyVersions: gql(`
    query TaxonomyVersionsHealthCheck($req: TaxonomyVersionsRequest!) {
      taxonomyVersions(req: $req) {
        versions
      }
    }
  `),
  TaxonomyLatestVersion: gql(`
    query TaxonomyLatestVersionHealthCheck($req: TaxonomyLatestVersionRequest!) {
      taxonomyLatestVersion(req: $req) {
        version
      }
    }
  `),
  CityInMsaInfo: gql(`
    query CityInMsaInfoHealthCheck($in: MSARefInfoInput!) {
      cityInMsaInfo(in: $in) {
        id
        longName
        metro_area
      }
    }
  `),
  SchoolInfo: gql(`
    query SchoolInfoHealthCheck($in: SchoolRefInfoInput!) {
      schoolInfo(in: $in) {
        id
        rsid
        name
      }
    }
  `),
  Ethnicity: gql(`
    query EthnicityHealthCheck {
      ethnicity {
        id
        name
      }
    }
  `),
  Gender: gql(`
    query GenderHealthCheck {
      gender {
        id
        name
      }
    }
  `),
  HighestDegree: gql(`
    query HighestDegreeHealthCheck {
      highestDegree {
        id
        name
      }
    }
  `),
  Seniority: gql(`
    query SeniorityHealthCheck {
      seniority {
        id
        name
      }
    }
  `),
  EmploymentRelationship: gql(`
    query EmploymentRelationshipHealthCheck {
      employmentRelationship {
        id
        name
      }
    }
  `),
  RemoteType: gql(`
    query RemoteTypeHealthCheck {
      remoteType {
        id
        name
      }
    }
  `),
  WorkSchedule: gql(`
    query WorkScheduleHealthCheck {
      workSchedule {
        id
        name
      }
    }
  `),
} as const;
